/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import BpmnAdapter from '~/components/Bpmn/BpmnAdapter';
import TaskComments from '~/components/comment/index';
import Details from '~/components/Details';
import {
  getTaskStatusImageAndLabel,
  TaskStatus,
} from '~/components/Details/getStatus';
import InstanceDetails from '~/components/Details/InstanceDetails';
import MainInstanceDetails from '~/components/Details/MainInstanceDetails';
import TabsContainer, { ITab } from '~/components/TabsContainer';
import listFixedDetails from '~/pages/Requests/RequestDetails/fixedDetails';
import listTaskFixedDetails from '~/pages/Tasks/TaskDetails/fixedDetails';
import { IState } from '~/store';
import { getStartEventRequest } from '~/store/modules/activities/actions';
import { getProcessInstanceRequest } from '~/store/modules/processes/actions';
import {
  getTaskListByProcessInstanceRequest,
  getTaskRequest,
} from '~/store/modules/tasks/actions';
import { ITask } from '~/store/modules/tasks/types';

interface TaskDetailsProps {
  taskInstance: ITask | undefined;
  open: boolean;
  modalState: (opened: boolean) => void;
}

const TaskDetails: React.FC<TaskDetailsProps> = ({
  taskInstance,
  open,
  modalState,
}) => {
  const [tab, setTab] = useState<number>(0);
  const [taskType, setTaskType] = useState<string>();
  const [task, setTask] = useState<ITask | null>(null);

  const processInstance = useSelector(
    (state: IState) => state.processes.solicitationInstance,
  );
  const loading = useSelector(
    (state: IState) => state.processes.loadingSolicitation,
  );
  const loadingTask = useSelector((state: IState) => state.tasks.loadingTask);

  const dispatch = useDispatch();

  useEffect(() => {
    if (!task && taskInstance) {
      setTask(taskInstance);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskInstance]);

  useEffect(() => {
    if (task)
      dispatch(
        getProcessInstanceRequest(task?.rootProcessInstanceId, 'solicitation'),
      );

    if (!open) {
      setTask(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, task]);

  useEffect(() => {
    return () => {
      setTab(0);
    };
  }, [open]);

  const requestTabs: ITab[] = [
    {
      label: 'Solicitação',
      value: 0,
      content: (
        <MainInstanceDetails
          process={processInstance}
          loading={loading || loadingTask}
          fixedDetails={listFixedDetails(processInstance)}
        />
      ),
    },
    {
      label: 'Tarefas',
      value: 1,
      disabled: !task,
      content: (
        <InstanceDetails
          parentLoading={loading}
          getfixedDetails={listTaskFixedDetails}
          instanceList={{
            find: () => {
              if (task?.processInstanceId)
                dispatch(
                  getTaskListByProcessInstanceRequest(task?.processInstanceId),
                );
            },
            pathToEntity: 'tasks',
            pathToInstance: 'processTaskList',
            pathToLoading: 'loadingTasksByProcessInstance',
          }}
          instanceDetails={{
            find: (task) => {
              if (task.type === 'userTask') dispatch(getTaskRequest(task.id));
              else if (task.type === 'startEvent')
                dispatch(getStartEventRequest(task.id));
              setTaskType(task.type);
            },
            pathToEntity: taskType === 'userTask' ? 'tasks' : 'activities',
            pathToInstance: taskType === 'userTask' ? 'task' : 'startEvent',
            pathToLoading:
              taskType === 'userTask' ? 'loadingTask' : 'loadingStartEvent',
          }}
          statusProps={{
            statuses: [
              TaskStatus.AVAILABLE,
              TaskStatus.IN_PROGRESS,
              TaskStatus.LATE,
              TaskStatus.COMPLETED,
              TaskStatus.COMPLETED_LATE,
              TaskStatus.DELETED,
            ],
            getStatus: getTaskStatusImageAndLabel,
          }}
        />
      ),
    },
    {
      label: 'Processo',
      value: 2,
      content: <BpmnAdapter processInstanceId={task?.processInstanceId} />,
    },
    {
      label: 'Comentários',
      value: 3,
      content: <TaskComments task={task} />,
    },
  ];

  return (
    <Details
      loading={loading || (!task && loadingTask)}
      title={task?.name}
      open={open}
      modalState={modalState}
    >
      <TabsContainer
        ariaLabel="task_tabs"
        value={tab}
        onChange={setTab}
        options={requestTabs}
      />
    </Details>
  );
};

export default TaskDetails;
